import { useState, useEffect } from "react";
import { apiFetch } from "../../api/api";
import { T } from "../data/Tokens";
import { Card, PageHeader, GhostBtn, StatCard, SectionTitle } from "../components/common/Ui";

function BarChart({ data, labels, color, height=130 }) {
  if (!data || data.length === 0) {
    return <p className="text-[11px] text-center py-8" style={{ color: T.textSec }}>No data yet.</p>;
  }
  const max = Math.max(...data, 1);
  const W=420, H=height, PL=28, PR=8, PT=10, PB=24;
  const cW=W-PL-PR, cH=H-PT-PB;
  const bw = Math.floor(cW/data.length)-6;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height }}>
      {[0,Math.round(max/2),max].map(v => {
        const y = PT+cH-((v/max)*cH);
        return <g key={v}><line x1={PL} y1={y} x2={W-PR} y2={y} stroke={T.border} strokeWidth="1"/>
          <text x={PL-4} y={y+4} textAnchor="end" fontSize="8" fill={T.textSec}>{v}</text></g>;
      })}
      {data.map((v,i) => {
        const x = PL + i*(cW/data.length) + 4;
        const bh = (v/max)*cH;
        return <g key={i}>
          <rect x={x} y={PT+cH-bh} width={bw} height={bh} rx="4" fill={color} opacity="0.85"/>
          <text x={x+bw/2} y={H-4} textAnchor="middle" fontSize="8" fill={T.textSec}>{labels[i]}</text>
        </g>;
      })}
    </svg>
  );
}

function LineChart({ data, labels }) {
  if (!data || data.length === 0) {
    return <p className="text-[11px] text-center py-8" style={{ color: T.textSec }}>No data yet.</p>;
  }
  const max = Math.max(...data, 1), min = 0;
  const W=420, H=130, PL=28, PR=8, PT=10, PB=24;
  const cW=W-PL-PR, cH=H-PT-PB;
  const pts = data.map((v,i) => [PL+(i/(data.length-1))*cW, PT+cH-((v-min)/(max-min))*cH]);
  const d = pts.map((p,i) => i===0 ? `M${p[0].toFixed(1)},${p[1].toFixed(1)}` : `L${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const fill = `${d} L${pts[pts.length-1][0]},${PT+cH} L${pts[0][0]},${PT+cH} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height:130 }}>
      <defs>
        <linearGradient id="lg2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={T.gold} stopOpacity="0.3"/>
          <stop offset="100%" stopColor={T.gold} stopOpacity="0.02"/>
        </linearGradient>
      </defs>
      {[min,Math.round(max/2),max].map(v => {
        const y=PT+cH-((v-min)/(max-min))*cH;
        return <g key={v}><line x1={PL} y1={y} x2={W-PR} y2={y} stroke={T.border} strokeWidth="1"/>
          <text x={PL-4} y={y+4} textAnchor="end" fontSize="8" fill={T.textSec}>{v}</text></g>;
      })}
      {labels.map((l,i) => {
        const x=PL+(i/(labels.length-1))*(W-PL-PR);
        return <text key={l} x={x} y={H-4} textAnchor="middle" fontSize="8" fill={T.textSec}>{l}</text>;
      })}
      <path d={fill} fill="url(#lg2)"/>
      <path d={d} fill="none" stroke={T.gold} strokeWidth="2.5" strokeLinecap="round"/>
      {pts.map(([x,y],i) => (
        <g key={i}><circle cx={x} cy={y} r="4" fill="white" stroke={T.gold} strokeWidth="2"/></g>
      ))}
    </svg>
  );
}

// ── Dummy — website visitor analytics (needs external tracking, kept as-is) ──
const STATS = [
  { icon:"👁️", label:"Sessions",     value:"24,821", sub:"↑ 18% this month",  color:T.success },
  { icon:"↩️", label:"Bounce Rate",  value:"38.2%",  sub:"↓ 4% improvement",  color:T.success },
  { icon:"🎯", label:"Conversion",   value:"3.8%",   sub:"↑ 0.6%",            color:T.success },
  { icon:"⏱️", label:"Avg Duration", value:"4m 22s", sub:"↑ 12s",             color:T.success },
  { icon:"📱", label:"Mobile %",     value:"68.4%",  sub:"of total traffic",  color:"#5B7EE5", bg:"#EEF3FF" },
  { icon:"🔄", label:"Return Rate",  value:"42.1%",  sub:"Returning visitors", color:T.gold },
];

const TRAFFIC = [
  { src:"Organic Search", pct:42, color:T.gold },
  { src:"Direct",         pct:28, color:"#8BAF8B" },
  { src:"Social Media",   pct:18, color:"#E8A8A8" },
  { src:"Email",          pct:8,  color:T.champagne },
  { src:"Referral",       pct:4,  color:"#C8C0B8" },
];

export default function AnalyticsAdmin() {
  const [period, setPeriod] = useState("month");
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

 useEffect(() => {
    setLoading(true);
    setLoadError("");
    apiFetch("/analytics/summary.php")
      .then(res => {
        console.log("API response:", res);   // isse console mein data dikhega
        setData({
          monthly: res.monthly || [],
          monthLabels: res.monthLabels || [],
          weekly: res.weekly || [],
          weekLabels: res.weekLabels || [],
          topProducts: res.topProducts || [],
          revenueByCategory: res.revenueByCategory || [],
        });
      })
      .catch(err => setLoadError(err.message || "Failed to load analytics."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <PageHeader title="Analytics" subtitle="Store performance insights">
        <div className="flex items-center gap-1 p-1 rounded-xl" style={{ background:T.bgSoft }}>
          {["week","month","year"].map(p => (
            <button key={p} onClick={() => setPeriod(p)}
              className="px-2.5 sm:px-3 py-1.5 rounded-lg text-[11px] sm:text-[12px] font-medium capitalize transition-all"
              style={period===p?{ background:"white", color:T.gold }:{ color:T.textSec }}>
              {p}
            </button>
          ))}
        </div>
        <GhostBtn>↓ Export PDF</GhostBtn>
      </PageHeader>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2.5 sm:gap-3 mb-5 sm:mb-6">
        {STATS.map(s => <StatCard key={s.label} {...s} />)}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 rounded-full animate-spin"
              style={{ border: `3px solid ${T.border}`, borderTopColor: T.gold }} />
            <p className="text-[12px]" style={{ color: T.textSec }}>Loading analytics...</p>
          </div>
        </div>
      )}

      {!loading && loadError && (
        <Card className="p-6 text-center mb-5">
          <p className="text-[13px] font-semibold mb-2" style={{ color: T.danger }}>{loadError}</p>
        </Card>
      )}

      {!loading && !loadError && data && (
        <>
          {/* Charts row 1 */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3.5 sm:gap-4 mb-4">
            <Card className="md:col-span-3 p-4 sm:p-5">
              <SectionTitle>Monthly Revenue (₹K)</SectionTitle>
              <LineChart data={data.monthly} labels={data.monthLabels} />
            </Card>
            <Card className="md:col-span-2 p-4 sm:p-5">
              <SectionTitle>Weekly Orders</SectionTitle>
              <BarChart data={data.weekly} labels={data.weekLabels} color={T.gold} height={130} />
            </Card>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3.5 sm:gap-4 mb-4">
            {/* Top products */}
            <Card className="md:col-span-3 overflow-hidden">
              <div className="px-4 sm:px-5 py-3.5 sm:py-4" style={{ borderBottom:`1px solid ${T.border}` }}>
                <SectionTitle>Top Selling Products</SectionTitle>
              </div>
              {data.topProducts.length === 0 ? (
                <p className="text-[12px] text-center py-8" style={{ color: T.textSec }}>No sales data yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[480px]">
                    <thead>
                      <tr style={{ borderBottom:`1px solid ${T.border}` }}>
                        <th className="px-3 sm:px-5 py-2.5 sm:py-3 text-left text-[10px] font-semibold uppercase tracking-wider" style={{ color:T.textSec }}>#</th>
                        <th className="px-3 sm:px-5 py-2.5 sm:py-3 text-left text-[10px] font-semibold uppercase tracking-wider" style={{ color:T.textSec }}>Product</th>
                        <th className="px-3 sm:px-5 py-2.5 sm:py-3 text-left text-[10px] font-semibold uppercase tracking-wider" style={{ color:T.textSec }}>Sales</th>
                        <th className="px-3 sm:px-5 py-2.5 sm:py-3 text-left text-[10px] font-semibold uppercase tracking-wider" style={{ color:T.textSec }}>Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.topProducts.map((p,i) => (
                        <tr key={p.name+i} className="transition-colors"
                          style={{ borderBottom:i<data.topProducts.length-1?`1px solid ${T.bgSoft}`:"" }}
                          onMouseEnter={e=>e.currentTarget.style.background=T.bgSoft}
                          onMouseLeave={e=>e.currentTarget.style.background=""}>
                          <td className="px-3 sm:px-5 py-2.5 sm:py-3">
                            <div className="w-6 h-6 rounded-lg flex items-center justify-center text-[11px] font-bold"
                              style={{ background:i===0?T.goldL:T.bgSoft, color:i===0?T.goldD:T.textSec }}>
                              {i+1}
                            </div>
                          </td>
                          <td className="px-3 sm:px-5 py-2.5 sm:py-3">
                            <p className="text-[12px] font-semibold" style={{ color:T.textPri }}>{p.name}</p>
                            <p className="text-[10px]" style={{ color:T.textSec }}>{p.category}</p>
                          </td>
                          <td className="px-3 sm:px-5 py-2.5 sm:py-3 text-[13px] font-bold" style={{ color:T.textPri }}>{p.sales}</td>
                          <td className="px-3 sm:px-5 py-2.5 sm:py-3 text-[13px] font-bold" style={{ color:T.gold }}>{p.revenue}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* Traffic sources */}
            <Card className="md:col-span-2 p-4 sm:p-5">
              <SectionTitle>Traffic Sources</SectionTitle>
              <div className="space-y-3 mt-2">
                {TRAFFIC.map(s => (
                  <div key={s.src}>
                    <div className="flex justify-between text-[11px] mb-1">
                      <span style={{ color:T.textSec }}>{s.src}</span>
                      <span style={{ color:T.textPri, fontWeight:600 }}>{s.pct}%</span>
                    </div>
                    <div className="h-2 rounded-full" style={{ background:T.bgSoft }}>
                      <div className="h-full rounded-full transition-all" style={{ width:`${s.pct}%`, background:s.color }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-4 space-y-2" style={{ borderTop:`1px solid ${T.border}` }}>
                <p className="text-[11px] font-semibold" style={{ color:T.textSec }}>Revenue by Category</p>
                {data.revenueByCategory.length === 0 ? (
                  <p className="text-[11px]" style={{ color: T.textSec }}>No data yet.</p>
                ) : (
                  data.revenueByCategory.map(c => (
                    <div key={c.label} className="flex items-center justify-between text-[12px]">
                      <span style={{ color:T.textSec }}>{c.label}</span>
                      <span style={{ color:T.textPri, fontWeight:600 }}>{c.pct}%</span>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}